import React, { useCallback, useState } from "react";
import { useFocusEffect } from "expo-router";
import analytics from "@react-native-firebase/analytics";

import ScrollableProfile from "./Components/ScrollableProfile";

import ScreenLayout from "@/Components/Layouts/Native/ScreenLayout";
import type { AppUserSession } from "@/schemas/auth/session";
import ProfileService from "@/services/user/profile";
import type { UserProfile } from "@/schemas/user/profile";
import { createProfileViewData, useTrackAnalytics } from "@/hooks/analytics";

// todo; pass in the user profile? TBD:
// https://github.com/dow-labs/Kai-Mobile/pull/582
type ProfileScreenProps = {
  session: AppUserSession;
  username: string;
};

function ProfileScreen({
  session,
  username,
}: ProfileScreenProps): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [targetProfile, setTargetProfile] = useState<UserProfile | null>(null);

  const fetchProfile = useCallback(async (): Promise<void> => {
    try {
      await analytics().logEvent("profile_view", {
        username: session.identity.username,
        target: username,
      });
    } catch (e) {
      // Analytics error is non-critical
    }

    try {
      setIsLoading(true);
      const profileService = new ProfileService(session);
      const profile = await profileService.getUserProfile(username);
      setTargetProfile(profile);
    } finally {
      setIsLoading(false);
    }
  }, [session, username]);

  useFocusEffect(
    useCallback(() => {
      fetchProfile();
    }, [fetchProfile]),
  );

  // Track profile view and its duration for analytics
  useTrackAnalytics(
    session.identity.username,
    (duration) => createProfileViewData(username, duration),
    [username],
  );

  return (
    <ScreenLayout isLoading={isLoading}>
      {targetProfile !== null && (
        <ScrollableProfile
          session={session}
          profile={targetProfile}
          onRefresh={fetchProfile}
        />
      )}
    </ScreenLayout>
  );
}

export default ProfileScreen;
